// ============================================================
// src/shack/shackUtils.js — Helpers puros / texturas del Shack 3D
// ============================================================

function woodRng(seed) {
  let s = seed || 1;
  return function() {
    s = (s * 16807 + 0) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

function drawImageContain(ctx, img, x, y, w, h) {
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (!iw || !ih) return;

  const scale = Math.min(w / iw, h / ih);
  const dw = iw * scale;
  const dh = ih * scale;
  const dx = x + ((w - dw) / 2);
  const dy = y + ((h - dh) / 2);
  ctx.drawImage(img, dx, dy, dw, dh);
}

function drawCertPlaceholder(ctx, item, width, height) {
  const g = ctx.createLinearGradient(0, 0, width, height);
  g.addColorStop(0, '#faf6ea');
  g.addColorStop(1, '#e8dcc0');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, width, height);

  ctx.fillStyle = 'rgba(120,90,40,0.10)';
  ctx.fillRect(24, 24, width - 48, height - 48);

  ctx.fillStyle = '#5b4120';
  ctx.textAlign = 'center';
  ctx.font = 'bold 54px serif';
  ctx.fillText('🏆', width / 2, height * 0.38);
  ctx.font = 'bold 28px Georgia, serif';
  ctx.fillText(item.concurso || 'Certificado', width / 2, height * 0.56);
  ctx.font = '18px monospace';
  ctx.fillStyle = 'rgba(60,40,20,0.65)';
  ctx.fillText(`${item.anio || '—'} · ${item.modo || '—'}`, width / 2, height * 0.66);
}

export function crearShadowTexture(T, size = 512) {
  const THREE = T();
  const cv = document.createElement('canvas');
  cv.width = size;
  cv.height = size;
  const ctx = cv.getContext('2d');

  const cx = size * 0.48;
  const cy = size * 0.52;
  const rg = ctx.createRadialGradient(cx, cy, size * 0.18, cx, cy, size * 0.56);
  rg.addColorStop(0, 'rgba(0,0,0,0.55)');
  rg.addColorStop(0.25, 'rgba(0,0,0,0.28)');
  rg.addColorStop(0.55, 'rgba(0,0,0,0.10)');
  rg.addColorStop(0.80, 'rgba(0,0,0,0.03)');
  rg.addColorStop(1, 'rgba(0,0,0,0.00)');
  ctx.fillStyle = rg;
  ctx.fillRect(0, 0, size, size);

  const gBot = ctx.createLinearGradient(0, size * 0.72, 0, size * 0.85);
  gBot.addColorStop(0, 'rgba(0,0,0,0.00)');
  gBot.addColorStop(1, 'rgba(0,0,0,0.18)');
  ctx.fillStyle = gBot;
  ctx.fillRect(size * 0.1, size * 0.72, size * 0.8, size * 0.13);

  const tex = new THREE.CanvasTexture(cv);
  tex.needsUpdate = true;
  return tex;
}

export function crearInnerShadowTexture(T, size = 256) {
  const THREE = T();
  const cv = document.createElement('canvas');
  cv.width = size;
  cv.height = size;
  const ctx = cv.getContext('2d');

  ctx.clearRect(0, 0, size, size);

  const edge = size * 0.30;
  const maxA = 0.60;

  const gTop = ctx.createLinearGradient(0, 0, 0, edge);
  gTop.addColorStop(0, `rgba(0,0,0,${maxA})`);
  gTop.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = gTop;
  ctx.fillRect(0, 0, size, edge);

  const gBot = ctx.createLinearGradient(0, size - edge, 0, size);
  gBot.addColorStop(0, 'rgba(0,0,0,0)');
  gBot.addColorStop(1, `rgba(0,0,0,${maxA})`);
  ctx.fillStyle = gBot;
  ctx.fillRect(0, size - edge, size, edge);

  const gLeft = ctx.createLinearGradient(0, 0, edge, 0);
  gLeft.addColorStop(0, `rgba(0,0,0,${maxA})`);
  gLeft.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = gLeft;
  ctx.fillRect(0, 0, edge, size);

  const gRight = ctx.createLinearGradient(size - edge, 0, size, 0);
  gRight.addColorStop(0, 'rgba(0,0,0,0)');
  gRight.addColorStop(1, `rgba(0,0,0,${maxA})`);
  ctx.fillStyle = gRight;
  ctx.fillRect(size - edge, 0, edge, size);

  const tex = new THREE.CanvasTexture(cv);
  tex.needsUpdate = true;
  return tex;
}

export function crearCertificateTexture(T, item, imgOrUrl, width = 1200, height = 800) {
  const THREE = T();
  const cv = document.createElement('canvas');
  cv.width = width;
  cv.height = height;
  const ctx = cv.getContext('2d');

  ctx.fillStyle = '#f8f3e8';
  ctx.fillRect(0, 0, width, height);

  const tex = new THREE.CanvasTexture(cv);

  const aplicarPostProcesado = () => {
    const rng = woodRng(77);
    for (let i = 0; i < 140; i++) {
      const x = rng() * width;
      const y = rng() * height;
      const len = 8 + rng() * 30;
      const ang = rng() * Math.PI;
      const alpha = 0.010 + rng() * 0.018;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(ang);
      ctx.strokeStyle = `rgba(150,130,100,${alpha})`;
      ctx.lineWidth = 0.4 + rng() * 0.8;
      ctx.beginPath();
      ctx.moveTo(-len / 2, 0);
      ctx.lineTo(len / 2, 0);
      ctx.stroke();
      ctx.restore();
    }

    const gAge = ctx.createRadialGradient(
      width / 2, height / 2, Math.min(width, height) * 0.3,
      width / 2, height / 2, Math.max(width, height) * 0.8
    );
    gAge.addColorStop(0, 'rgba(0,0,0,0)');
    gAge.addColorStop(1, 'rgba(110,80,30,0.10)');
    ctx.fillStyle = gAge;
    ctx.fillRect(0, 0, width, height);
    tex.needsUpdate = true;
  };

  const dibujarImagen = (img) => {
    if (img.naturalWidth && img.naturalHeight) {
      item.imgRatio = img.naturalWidth / img.naturalHeight;
    }
    ctx.fillStyle = '#fbf8f0';
    ctx.fillRect(0, 0, width, height);
    drawImageContain(ctx, img, 0, 0, width, height);
    aplicarPostProcesado();
  };

  if (imgOrUrl instanceof HTMLImageElement && imgOrUrl.complete && imgOrUrl.naturalWidth) {
    dibujarImagen(imgOrUrl);
  } else if (typeof imgOrUrl === 'string' && imgOrUrl) {
    const img = new Image();
    img.onload = () => dibujarImagen(img);
    img.onerror = () => {
      drawCertPlaceholder(ctx, item, width, height);
      aplicarPostProcesado();
    };
    img.src = imgOrUrl;
    if (img.complete && img.naturalWidth) dibujarImagen(img);
  } else {
    drawCertPlaceholder(ctx, item, width, height);
    aplicarPostProcesado();
  }

  return tex;
}